import { createSlice } from "@reduxjs/toolkit";
import {
  fetchDynamicData,
  getAdmissionsList,
  getBranchWiseDailyComparisonData,
  getLatestAdmissionOrders,
  getProjectedRevenue,
  getTopCourses,
} from "../utils/Apis";

const dynamicSlice = createSlice({
  name: "dynamic",
  initialState: {},
  reducers: {
    resetTabData: (state, action) => {
      const tab = action.payload;
      delete state[tab];
    },
    addStudentToDynamic: (state, action) => {
      state.students.data.push(action.payload); // simple push to students.data
    },
    setTabData: (state, action) => {
      const { tab, data, pagination } = action.payload;
      state[tab] = {
        data,
        pagination: pagination || {
          currentPage: 1,
          totalPages: 1,
          perPage: 100,
          totalItems: Array.isArray(data) ? data.length : 0,
        },
        loading: false,
        error: null,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDynamicData.pending, (state, action) => {
        const tab = action.meta.arg.tab;
        state[tab] = {
          ...(state[tab] || {}),
          loading: true,
          error: null,
        };
      })
      .addCase(fetchDynamicData.fulfilled, (state, action) => {
        const { tab, data } = action.payload;

        state[tab] = {
          data: data?.items || data, // Main array of items
          pagination: {
            currentPage: data?.currentPage || 1,
            totalPages: data?.totalPages || 1,
            perPage: data?.perPage || 100,
            totalItems:
              data?.totalItems || (Array.isArray(data) ? data.length : 0),
          },
          loading: false,
          error: null,
        };
      })
      .addCase(fetchDynamicData.rejected, (state, action) => {
        const tab = action.payload?.tab || action.meta.arg.tab;
        state[tab] = {
          ...(state[tab] || {}),
          loading: false,
          error: action.payload?.error || "Something went wrong",
        };
      })
      .addCase(getProjectedRevenue.pending, (state, action) => {
        const tab = action.meta.arg?.tab || "projectedRevenue";
        state[tab] = {
          loading: true,
          error: null,
          data: 0, // optional: loading पर data reset करना चाहते हो तो रखें
        };
      })
      .addCase(getProjectedRevenue.fulfilled, (state, action) => {
        const { tab = "projectedRevenue", data } = action.payload || {};
        state[tab] = {
          loading: false,
          error: null,
          data,
        };
      })
      .addCase(getProjectedRevenue.rejected, (state, action) => {
        // error message कई जगह से आ सकता है, fallback रखो
        const tab =
          action.payload?.tab || action.meta.arg?.tab || "projectedRevenue";
        const errorMessage =
          action.payload?.message ||
          action.error?.message ||
          "An error occurred";

        state[tab] = {
          loading: false,
          error: errorMessage,
          data: 0,
        };
      })
      .addCase(getLatestAdmissionOrders.pending, (state, action) => {
        const tab = action.meta.arg?.tab || "latestAdmissionOrders";
        state[tab] = {
          loading: true,
          error: null,
          data: 0,
        };
      })
      .addCase(getLatestAdmissionOrders.fulfilled, (state, action) => {
        const { tab = "latestAdmissionOrders", data } = action.payload || {};
        state[tab] = {
          loading: false,
          error: null,
          data,
        };
      })
      .addCase(getLatestAdmissionOrders.rejected, (state, action) => {
        const tab =
          action.payload?.tab ||
          action.meta.arg?.tab ||
          "latestAdmissionOrders";
        const errorMessage =
          action.payload?.message ||
          action.error?.message ||
          "An error occurred";

        state[tab] = {
          loading: false,
          error: errorMessage,
          data: 0,
        };
      })
      .addCase(getTopCourses.pending, (state, action) => {
        const tab = action.meta.arg?.tab || "topCourses";
        state[tab] = {
          loading: true,
          error: null,
          data: 0, // Optional: loading पर data reset करना है तो
        };
      })
      .addCase(getTopCourses.fulfilled, (state, action) => {
        const { tab = "topCourses", data } = action.payload || {};
        state[tab] = {
          loading: false,
          error: null,
          data,
        };
      })
      .addCase(getTopCourses.rejected, (state, action) => {
        const tab = action.payload?.tab || action.meta.arg?.tab || "topCourses";
        const errorMessage =
          action.payload?.message ||
          action.error?.message ||
          "An error occurred";

        state[tab] = {
          loading: false,
          error: errorMessage,
          data: 0,
        };
      })
      .addCase(getAdmissionsList.pending, (state, action) => {
        const tab = action.meta.arg?.tab || "latestAdmissionList";
        state[tab] = {
          loading: true,
          error: null,
          data: {
            data: [],
            total: 0,
          }, // reset data to avoid showing stale data while loading
        };
      })

      .addCase(getAdmissionsList.fulfilled, (state, action) => {
        const { tab = "latestAdmissionList", data } = action.payload || {};
        state[tab] = {
          loading: false,
          error: null,
          data: {
            data: data?.data || [],
            total: data?.total || 0,
          },
        };
      })

      .addCase(getAdmissionsList.rejected, (state, action) => {
        const tab =
          action.payload?.tab || action.meta.arg?.tab || "latestAdmissionList";
        const errorMessage =
          action.payload?.message ||
          action.error?.message ||
          "An error occurred while fetching admissions";

        state[tab] = {
          loading: false,
          error: errorMessage,
          data: {
            data: [],
            total: 0,
          },
        };
      })
      .addCase(getBranchWiseDailyComparisonData.pending, (state, action) => {
        const tab = action.meta.arg?.tab || "branchWiseComparison";
        state[tab] = {
          loading: true,
          error: null,
          data: 0, // या null, जैसे आपकी app preference हो
        };
      })
      .addCase(getBranchWiseDailyComparisonData.fulfilled, (state, action) => {
        const { tab = "branchWiseComparison", data } = action.payload || {};
        state[tab] = {
          loading: false,
          error: null,
          data,
        };
      })
      .addCase(getBranchWiseDailyComparisonData.rejected, (state, action) => {
        const tab =
          action.payload?.tab || action.meta.arg?.tab || "branchWiseComparison";
        const errorMessage =
          action.payload?.message ||
          action.error?.message ||
          "An error occurred while fetching comparison data";

        state[tab] = {
          loading: false,
          error: errorMessage,
          data: 0,
        };
      });
  },
});

export const { resetTabData, setTabData, addStudentToDynamic } =
  dynamicSlice.actions;
export const selectTabData = (state, tab) => state.dynamic[tab]?.data || [];
export const selectTabPagination = (state, tab) =>
  state.dynamic[tab]?.pagination || {};
export const selectTabLoading = (state, tab) => state.dynamic[tab]?.loading;
export default dynamicSlice.reducer;
